import {
  FIELD_TYPE_CATALOGUE,
  formatFieldValue,
  isConditionComplete,
  type FilterCondition,
  type FilterOperator,
} from '@crm/contracts';
import { ChevronDown, Plus, X } from 'lucide-react';
import { useEffect, useId, useRef, useState } from 'react';
import { describeError } from '../../lib/api.ts';
import { FieldEditor } from '../fields/editor.tsx';
import { Button } from '../ui/button.tsx';
import { Input } from '../ui/input.tsx';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover.tsx';
import { FilterBar, OP_LABEL, type FilterField } from './filter-bar.tsx';
import { CheckList, OptionSearch, STAGE_GROUPS } from './option-search.tsx';
import { useDebounced } from './use-debounced.ts';

/**
 * Jira-like filters: a chip per primary field beside the search, a removable chip per other
 * field in use, and "Add filter" for the rest. With `advanced` (flag `filters.dsl_builder`) the
 * Add filter panel IS the FilterBar's condition builder and the bar shows no chips. Every control
 * reads and writes the same flat AND of conditions, so URL, saved views, table and board are
 * unchanged.
 */
interface Props {
  fields: readonly FilterField[];
  conditions: FilterCondition[];
  onChange: (next: FilterCondition[]) => void;
}

const MIN_CHARS = 3;
// One quick choice per field: a value list, a single value and a "contains" replace each other.
const VALUE_OPS: FilterOperator[] = ['in', 'eq', 'contains'];
const NO_VALUE: FilterOperator[] = ['is_empty', 'is_not_empty'];
const supports = (field: FilterField, op: FilterOperator) =>
  FIELD_TYPE_CATALOGUE[field.type].operators.includes(op);

/** `conditions` with `field`'s conditions in `ops` swapped for `next`, kept at the first one's place. */
function putCondition(
  conditions: FilterCondition[],
  field: string,
  ops: FilterOperator[],
  next?: FilterCondition,
): FilterCondition[] {
  const hit = (c: FilterCondition) => c.field === field && ops.includes(c.op);
  const at = conditions.findIndex(hit);
  const rest = conditions.filter((c) => !hit(c));
  if (next) rest.splice(at === -1 ? rest.length : at, 0, next);
  return rest;
}

function showValue(field: FilterField, v: unknown): string {
  if (v === undefined || v === null) return '';
  if (typeof v === 'boolean') return v ? 'Yes' : 'No';
  if (typeof v === 'number') return formatFieldValue(v, field.format);
  if (typeof v === 'object') {
    const c = v as { amountMinor?: number; currency?: string };
    return typeof c.amountMinor === 'number'
      ? `${c.amountMinor / 100} ${c.currency ?? ''}`.trim()
      : JSON.stringify(v);
  }
  const s = `${v as string | number}`;
  return field.options?.find((o) => o.value === s)?.label ?? s;
}

/** "Lost" when the values are exactly every stage of one or more groups. */
function wholeGroups(field: FilterField, values: unknown[]): string | undefined {
  const of = (group: string) => (field.options ?? []).filter((o) => o.group === group);
  const full = STAGE_GROUPS.filter(
    ({ group }) => of(group).length > 0 && of(group).every((o) => values.includes(o.value)),
  );
  const covered = full.reduce((n, { group }) => n + of(group).length, 0);
  return full.length > 0 && covered === values.length
    ? full.map((g) => g.label).join(', ')
    : undefined;
}

/** A field's chip text; a Min and a Max read as one range. */
function describeAll(field: FilterField, own: FilterCondition[]): string {
  const min = own.find((c) => c.op === 'gte');
  const max = own.find((c) => c.op === 'lte');
  if (!min || !max) return own.map((c) => describe(field, c)).join(', ');
  return [
    `${field.label} is between ${showValue(field, min.value)} and ${showValue(field, max.value)}`,
    ...own.filter((c) => c !== min && c !== max).map((c) => describe(field, c)),
  ].join(', ');
}

function describe(field: FilterField, c: FilterCondition): string {
  if (NO_VALUE.includes(c.op)) return `${field.label} ${OP_LABEL[c.op]}`;
  const values: unknown[] = Array.isArray(c.value) ? c.value : [c.value];
  if (c.op === 'in' || c.op === 'eq')
    return `${field.label}: ${values.length === 1 ? showValue(field, values[0]) : (wholeGroups(field, values) ?? values.length)}`;
  if (c.op === 'contains') return `${field.label} contains "${showValue(field, c.value)}"`;
  return `${field.label} ${OP_LABEL[c.op]} ${values.map((v) => showValue(field, v)).join(', ')}`;
}

function TextSearch({
  field,
  contains,
  selected,
  onToggle,
  onContains,
}: {
  field: FilterField;
  contains: string;
  selected: string[];
  onToggle: (value: string) => void;
  onContains: (text: string) => void;
}) {
  const [text, setText] = useState(contains);
  const typed = text.trim();
  const q = useDebounced(typed, 300);
  const [result, setResult] = useState<{ q: string; values?: string[]; error?: string }>();
  // Pages may rebuild `fields` on every render; only a new text should ask again.
  const hints = useRef(field.hints);
  hints.current = field.hints;
  const hasHints = field.hints !== undefined;

  useEffect(() => {
    const ask = hints.current;
    if (!ask || q.length < MIN_CHARS) return;
    let latest = true;
    void ask(q).then(
      (values) => latest && setResult({ q, values: [...new Set(values)] }),
      (err: unknown) => latest && setResult({ q, error: describeError(err) }),
    );
    return () => {
      latest = false;
    };
  }, [q, hasHints]);

  const ready = typed === q && result?.q === q;
  const short = typed.length < MIN_CHARS;
  const suggestions = !short && ready ? (result.values ?? []) : [];
  const items = [...new Set([...selected, ...suggestions])].map((v) => ({ value: v, label: v }));
  const minId = useId();

  return (
    <>
      <Input
        aria-label={`Search ${field.label}`}
        aria-describedby={short ? minId : undefined}
        placeholder={`Search ${field.label}`}
        value={text}
        onChange={(e) => setText(e.target.value)}
      />
      {short && (
        <p id={minId} className="text-muted-foreground px-2 text-xs">
          Type at least 3 characters
        </p>
      )}
      {!short && supports(field, 'contains') && (
        <button
          type="button"
          aria-pressed={contains === typed}
          className="rounded-md px-2 py-1.5 text-left text-sm hover:bg-(--v2-dropdown-bg-hover) aria-pressed:bg-(--v2-dropdown-bg-active)"
          onClick={() => onContains(typed)}
        >
          Contains &quot;{typed}&quot;
        </button>
      )}
      {hasHints && (
        // Kept mounted so screen readers announce its changes.
        <p role="status" className="text-muted-foreground px-2 text-xs empty:sr-only">
          {short
            ? null
            : !ready
              ? 'Loading suggestions…'
              : result.error === undefined && suggestions.length === 0
                ? 'No suggestions'
                : null}
        </p>
      )}
      {!short && ready && result.error !== undefined && (
        <p role="alert" className="text-destructive px-2 text-xs">
          Could not load suggestions: {result.error}
        </p>
      )}
      <CheckList
        label={`${field.label} values`}
        items={items}
        selected={selected}
        onToggle={onToggle}
      />
    </>
  );
}

/** The controls for one field, shared by a chip's popover and the Add filter side panel. */
function FieldPanel({
  field,
  conditions,
  onChange,
  single,
}: Omit<Props, 'fields'> & { field: FilterField; single?: boolean | undefined }) {
  const id = useId();
  const own = conditions.filter((c) => c.field === field.name);
  const find = (op: FilterOperator) => own.find((c) => c.op === op);
  const set = (ops: FilterOperator[], next?: FilterCondition) =>
    onChange(putCondition(conditions, field.name, ops, next));

  const selected = own
    .filter((c) => c.op === 'in' || c.op === 'eq')
    .flatMap((c) => (Array.isArray(c.value) ? (c.value as unknown[]) : [c.value]))
    .map((v) => String(v as string));
  const pick = (values: string[]) =>
    set(
      VALUE_OPS,
      values.length === 0
        ? undefined
        : supports(field, 'in')
          ? { field: field.name, op: 'in', value: values }
          : // A type without `in` holds one value at a time.
            { field: field.name, op: 'eq', value: values.at(-1) },
    );
  const toggle = (v: string) =>
    pick(selected.includes(v) ? selected.filter((x) => x !== v) : [...selected, v]);
  const { prefix, suffix } = field.format ?? {};

  let body;
  if (field.options) {
    body = (
      <OptionSearch
        label={field.label}
        options={field.options}
        selected={selected}
        onChange={pick}
        single={single}
      />
    );
  } else if (field.type === 'boolean') {
    body = (
      <div className="flex gap-2">
        {[true, false].map((b) => {
          const on = find('eq')?.value === b;
          return (
            <Button
              key={String(b)}
              size="sm"
              variant={on ? 'default' : 'outline'}
              aria-pressed={on}
              onClick={() =>
                set(['eq'], on ? undefined : { field: field.name, op: 'eq', value: b })
              }
            >
              {b ? 'Yes' : 'No'}
            </Button>
          );
        })}
      </div>
    );
  } else if (supports(field, 'gte')) {
    const dated = field.type === 'date' || field.type === 'datetime';
    body = (['gte', 'lte'] as const).map((op) => {
      const label = op === 'gte' ? (dated ? 'From' : 'Min') : dated ? 'To' : 'Max';
      return (
        <div key={op} className="flex flex-col gap-1">
          <label htmlFor={`${id}-${op}`} className="text-muted-foreground px-2 text-xs">
            {label}
          </label>
          <div className="flex items-center gap-1.5">
            {prefix && <span className="text-muted-foreground text-sm">{prefix}</span>}
            <FieldEditor
              id={`${id}-${op}`}
              type={field.type}
              label={label}
              value={find(op)?.value ?? null}
              onChange={(v) =>
                set([op], v === null || v === '' ? undefined : { field: field.name, op, value: v })
              }
            />
            {suffix && <span className="text-muted-foreground text-sm">{suffix}</span>}
          </div>
        </div>
      );
    });
  } else {
    const contains = find('contains')?.value;
    body = (
      <TextSearch
        field={field}
        contains={typeof contains === 'string' ? contains : ''}
        selected={selected}
        onToggle={toggle}
        onContains={(text) =>
          contains === text
            ? set(['contains'])
            : set(VALUE_OPS, { field: field.name, op: 'contains', value: text })
        }
      />
    );
  }
  return (
    <div data-testid="filter-panel" className="flex w-64 max-w-full flex-col gap-2">
      <p className="text-muted-foreground px-2 text-xs font-medium">{field.label}</p>
      {body}
    </div>
  );
}

/**
 * `unknown`: a field this list cannot offer (hidden for the role, no longer indexed), so no panel.
 * `single`: one option only (radios) — the Projects bulk add picks its stage with this chip.
 * `block`: drawn as a form input there — the full width, outlined even with a value, no remove.
 */
export function FieldChip({
  field,
  conditions,
  onChange,
  unknown,
  single,
  block,
}: Omit<Props, 'fields'> & {
  field: FilterField;
  unknown?: boolean;
  single?: boolean;
  block?: boolean;
}) {
  // A row still being filled in the builder is not a filter yet.
  const own = conditions.filter((c) => c.field === field.name && isConditionComplete(c));
  const active = own.length > 0;
  const label = active ? describeAll(field, own) : field.label;
  const chip = {
    size: block ? 'default' : 'sm',
    variant: active && !block ? 'default' : 'outline',
    'data-active': active,
    'data-testid': `quick-filter-${field.name}`,
    className: block ? 'w-full justify-between' : active ? 'max-w-64 rounded-r-none' : 'max-w-64',
    title: label,
  } as const;
  return (
    <span className={block ? 'flex w-full items-center' : 'inline-flex items-center'}>
      {unknown ? (
        <Button asChild {...chip}>
          <span>
            <span className="truncate">{label}</span>
          </span>
        </Button>
      ) : (
        // Inside a dialog (`block`) the popover is portalled out of it, where the dialog's scroll
        // lock cancels the wheel; a modal popover takes its own lock, so its list scrolls.
        <Popover modal={block ?? false}>
          <PopoverTrigger asChild>
            <Button {...chip}>
              <span className="truncate">{label}</span>
              <ChevronDown />
            </Button>
          </PopoverTrigger>
          <PopoverContent aria-label={`${field.label} filter`}>
            <FieldPanel field={field} conditions={conditions} onChange={onChange} single={single} />
          </PopoverContent>
        </Popover>
      )}
      {active && !block && (
        <Button
          size="icon-sm"
          variant="default"
          className="rounded-l-none"
          aria-label={`Remove ${field.label} filter`}
          onClick={() => onChange(conditions.filter((c) => c.field !== field.name))}
        >
          <X />
        </Button>
      )}
    </span>
  );
}

function AddFilter({
  fields,
  allFields,
  conditions,
  onChange,
  advanced,
}: Props & { allFields: readonly FilterField[]; advanced: boolean }) {
  const [search, setSearch] = useState('');
  const [active, setActive] = useState<{ name: string; focus: boolean } | null>(null);
  const panel = useRef<HTMLDivElement>(null);
  const needle = search.trim().toLowerCase();
  const field = fields.find((f) => f.name === active?.name);
  // The bar shows no chips with the builder, so the trigger says how many filters apply.
  const count = advanced ? conditions.filter(isConditionComplete).length : 0;

  // A click (or Enter) moves into the panel; a hover only shows it.
  useEffect(() => {
    if (active?.focus) panel.current?.querySelector<HTMLElement>('input, button')?.focus();
  }, [active]);

  const item = (name: string, label: string) => (
    <button
      type="button"
      aria-pressed={active?.name === name}
      className="w-full truncate rounded-md px-2 py-1.5 text-left text-sm hover:bg-(--v2-dropdown-bg-hover) aria-pressed:bg-(--v2-dropdown-bg-active)"
      // A hover never replaces a panel the user moved into: typed text would be lost.
      onMouseEnter={() => setActive((a) => (a?.focus ? a : { name, focus: false }))}
      onClick={() => setActive({ name, focus: true })}
    >
      {label}
    </button>
  );

  return (
    <Popover
      onOpenChange={(open) => {
        if (advanced) {
          // The builder opens ready to fill, and closing drops the rows nobody filled in.
          const first = allFields[0];
          if (open && conditions.length === 0 && first)
            onChange([{ field: first.name, op: FIELD_TYPE_CATALOGUE[first.type].operators[0]! }]);
          const filled = conditions.filter(isConditionComplete);
          if (!open && filled.length !== conditions.length) onChange(filled);
        }
        if (!open) {
          setActive(null);
          setSearch('');
        }
      }}
    >
      <PopoverTrigger asChild>
        <Button size="sm" variant="ghost" data-testid="add-filter-menu">
          <Plus /> Add filter{count > 0 && ` (${count})`}
        </Button>
      </PopoverTrigger>
      <PopoverContent aria-label="Add filter" className="flex flex-col gap-3">
        {advanced ? (
          <div data-testid="advanced-panel" className="w-[38rem] max-w-full">
            <FilterBar
              fields={allFields}
              conditions={conditions}
              onChange={onChange}
              addLabel="Add condition"
            />
          </div>
        ) : (
          <div className="flex flex-col gap-3 sm:flex-row">
            {fields.length > 0 && (
              <div className="flex w-56 max-w-full shrink-0 flex-col gap-2">
                <Input
                  aria-label="Search fields"
                  placeholder="Search fields"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <ul aria-label="Fields" className="max-h-72 overflow-auto">
                  {fields
                    .filter((f) => f.label.toLowerCase().includes(needle))
                    .map((f) => (
                      <li key={f.name}>{item(f.name, f.label)}</li>
                    ))}
                </ul>
              </div>
            )}
            {field && (
              <div ref={panel} className="border-t pt-3 sm:border-t-0 sm:border-l sm:pt-0 sm:pl-3">
                <FieldPanel
                  key={field.name}
                  field={field}
                  conditions={conditions}
                  onChange={onChange}
                />
              </div>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}

export function QuickFilters({
  fields,
  conditions,
  onChange,
  advanced,
}: Props & { advanced?: boolean }) {
  const others = fields.filter((f) => !f.primary);
  const filled = conditions.filter(isConditionComplete);
  const unknown = [...new Set(filled.map((c) => c.field))].filter(
    (name) => !fields.some((f) => f.name === name),
  );
  return (
    <div data-testid="quick-filters" className="flex flex-wrap items-center gap-2">
      {!advanced && (
        <>
          {fields
            .filter((f) => f.primary)
            .map((f) => (
              <FieldChip key={f.name} field={f} conditions={conditions} onChange={onChange} />
            ))}
          {others
            .filter((f) => filled.some((c) => c.field === f.name))
            .map((f) => (
              <FieldChip key={f.name} field={f} conditions={conditions} onChange={onChange} />
            ))}
          {unknown.map((name) => (
            <FieldChip
              key={name}
              field={{ name, label: name, type: 'text' }}
              conditions={conditions}
              onChange={onChange}
              unknown
            />
          ))}
        </>
      )}
      {(others.length > 0 || advanced) && (
        <AddFilter
          fields={others}
          allFields={fields}
          conditions={conditions}
          onChange={onChange}
          advanced={advanced ?? false}
        />
      )}
      {filled.length > 0 && (
        <Button size="sm" variant="link" onClick={() => onChange([])}>
          Clear filters
        </Button>
      )}
    </div>
  );
}
